import { useEffect, useState } from "react";
import type { z } from "zod";
import type { AccessResponse, ChatConfig } from "@crashx/shared";
import { useConnectionStore } from "../../state/connectionStore.js";
import { chatApi, chatError } from "./chatApi.js";
import { useChatStore } from "./chatStore.js";

export function useChatAccess() {
  const status = useConnectionStore((state) => state.status);
  const epoch = useChatStore((state) => state.epoch);
  const [access, setAccess] = useState<z.infer<typeof AccessResponse> | null>(null);
  const [accessEpoch, setAccessEpoch] = useState(-1);
  const [config, setConfig] = useState<z.infer<typeof ChatConfig> | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (status !== "connected") return;
    let active = true;
    let revision = 0;
    const refresh = async () => {
      const current = ++revision;
      try {
        const [nextAccess, nextConfig] = await Promise.all([chatApi.access(), chatApi.config()]);
        if (!active || current !== revision) return;
        setAccess(nextAccess);
        setAccessEpoch(epoch);
        setConfig(nextConfig);
        setError(null);
      } catch (cause) {
        if (active && current === revision) {
          setAccess(null);
          setError(chatError(cause));
        }
      }
    };
    const focus = () => {
      void refresh();
    };
    void refresh();
    window.addEventListener("focus", focus);
    return () => {
      active = false;
      window.removeEventListener("focus", focus);
    };
  }, [status, epoch]);
  return { access: accessEpoch === epoch ? access : null, config, error, connected: status === "connected" };
}
